import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { adminApi } from '../api'
import type { AppVersionRow, AppUpdateSetting } from '../api/appVersion'
import { toastUtil } from '../../utils/toast'
import AdminSidebar from '../components/AdminSidebar'

const RANGES = [
  { days: 7, label: '최근 7일' },
  { days: 30, label: '최근 30일' },
  { days: 0, label: '전체' },
]

const PLATFORM_LABEL: Record<string, string> = {
  ios: 'iOS',
  android: 'Android',
}

const formatDate = (iso: string | null): string => {
  if (!iso) return '-'
  const d = new Date(iso.replace(' ', 'T'))
  if (Number.isNaN(d.getTime())) return '-'
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(
    d.getDate()
  ).padStart(2, '0')}`
}

/**
 * 앱 버전 분포.
 *
 * 값은 앱이 요청 헤더로 보낸 것을 서버가 사용자 행에 남긴 것이다
 * (RecordAppVersion 미들웨어). 버전을 보내는 기능이 들어간 앱부터 집계되므로,
 * 그 이전 버전을 쓰는 사람은 전부 '모름'으로 잡힌다.
 */
const AdminAppVersions: React.FC = () => {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(true)
  const [days, setDays] = useState(30)
  const [rows, setRows] = useState<AppVersionRow[]>([])
  const [total, setTotal] = useState(0)
  const [unknown, setUnknown] = useState(0)
  const [setting, setSetting] = useState<AppUpdateSetting | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  const fetchVersions = useCallback(
    async (range: number) => {
      setIsLoading(true)
      try {
        const res = await adminApi.getAppVersions(range)
        setRows(res.data.versions)
        setTotal(res.data.total)
        setUnknown(res.data.unknown)
      } catch (error: any) {
        if (error.response?.status === 401 || error.response?.status === 403) {
          navigate('/admin/login')
        } else {
          toastUtil.error('앱 버전을 불러오는데 실패했습니다')
        }
      } finally {
        setIsLoading(false)
      }
    },
    [navigate]
  )

  const fetchSetting = useCallback(async () => {
    try {
      const res = await adminApi.getAppUpdateSetting()
      setSetting(res.data)
    } catch {
      // 설정을 못 읽어도 분포는 볼 수 있게 화면을 막지 않는다
    }
  }, [])

  useEffect(() => {
    const adminToken = localStorage.getItem('admin_token')
    if (!adminToken) {
      navigate('/admin/login')
      return
    }
    fetchVersions(days)
    fetchSetting()
  }, [navigate, fetchVersions, fetchSetting, days])

  const saveSetting = async () => {
    if (!setting) return
    setIsSaving(true)
    try {
      const res = await adminApi.updateAppUpdateSetting(setting)
      setSetting(res.data)
      toastUtil.success('저장되었습니다')
    } catch (error: any) {
      toastUtil.error(
        error.response?.data?.message || '저장 중 오류가 발생했습니다'
      )
    } finally {
      setIsSaving(false)
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('admin_token')
    navigate('/admin/login')
  }

  const known = rows.filter((r) => r.version !== null)
  const knownTotal = known.reduce((sum, r) => sum + r.users, 0)

  return (
    <div className="min-h-screen bg-backgroundColorBg2 flex">
      <AdminSidebar />

      <div className="flex-1 flex flex-col">
        <header className="bg-white border-b border-brandPrimary-10% sticky top-0 z-10">
          <div className="px-24px py-16px flex items-center justify-end">
            <button
              onClick={handleLogout}
              className="text-style-button2 text-textColorText2 hover:text-brown-80%-main transition-colors"
            >
              로그아웃
            </button>
          </div>
        </header>

        <main className="flex-1 px-24px py-32px">
          <h2 className="text-style-title2 text-textColorText2 mb-8px">
            앱 버전
          </h2>
          <p className="text-style-captionL text-textColorText2 mb-24px">
            기간 안에 실제로 접속한 사람의 앱 버전입니다. &apos;전체&apos;는 활성 회원
            전부입니다. 최근 접속자 중 &apos;모름&apos;은 대개 앱 없이 웹 브라우저로
            들어온 사람입니다.
          </p>

          {setting && (
            <div className="max-w-[900px] bg-white rounded-16px shadow-sm p-24px mb-24px">
              <h3 className="font-pretendard font-bold text-18px text-textColorText2 mb-8px">
                강제 업데이트 안내
              </h3>
              <p className="text-style-captionL text-textColorText2 mb-16px">
                최소 버전보다 낮은 앱은 업데이트 안내 화면으로 덮입니다. 앱을
                못 쓰게 만드는 설정이라, <b>스토어에 그 버전이 실제로 올라간
                뒤에만</b> 켜세요.
              </p>

              <label className="flex items-center gap-8px mb-16px cursor-pointer">
                <input
                  type="checkbox"
                  checked={setting.is_enabled}
                  onChange={(e) =>
                    setSetting({ ...setting, is_enabled: e.target.checked })
                  }
                />
                <span className="text-style-body2 text-textColorText2">
                  기능 켜기 (끄면 아무에게도 안 뜹니다)
                </span>
              </label>

              <div className="flex gap-16px mb-16px">
                {(
                  [
                    ['ios_min_version', 'iOS 최소 버전'],
                    ['android_min_version', '안드로이드 최소 버전'],
                  ] as const
                ).map(([key, label]) => (
                  <div key={key}>
                    <div className="text-style-captionL text-textColorText2 mb-4px">
                      {label}
                    </div>
                    <input
                      type="text"
                      placeholder="예: 1.0.42 (비우면 요구 안 함)"
                      value={setting[key] ?? ''}
                      onChange={(e) =>
                        setSetting({ ...setting, [key]: e.target.value })
                      }
                      className="w-220px px-12px py-8px rounded-8px border border-brandPrimary-10% text-style-body2"
                    />
                  </div>
                ))}
              </div>

              <label className="flex items-start gap-8px mb-16px cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-4px"
                  checked={setting.block_unknown_version}
                  onChange={(e) =>
                    setSetting({
                      ...setting,
                      block_unknown_version: e.target.checked,
                    })
                  }
                />
                <span className="text-style-body2 text-textColorText2">
                  버전을 안 보내는 앱도 구버전으로 취급
                  <br />
                  <span className="text-style-captionL">
                    앱이 자기 버전을 알려주는 기능은 최근에 들어갔습니다. 그
                    이전 빌드는 버전을 안 보내므로 서버가 알 수 없습니다. 정작
                    업데이트가 필요한 게 그 앱들이라 이 옵션이 필요하지만,
                    버전을 보내는 빌드가 스토어에 올라간 뒤에 켜야 합니다.
                  </span>
                </span>
              </label>

              <button
                type="button"
                onClick={saveSetting}
                disabled={isSaving}
                className="px-20px py-10px rounded-8px bg-brown-80%-main text-white text-style-button2 disabled:opacity-50"
              >
                {isSaving ? '저장 중…' : '저장'}
              </button>
            </div>
          )}

          <div className="flex gap-8px mb-24px">
            {RANGES.map((r) => (
              <button
                key={r.days}
                type="button"
                onClick={() => setDays(r.days)}
                className={`px-16px py-8px rounded-8px text-style-button2 transition-colors ${
                  days === r.days
                    ? 'bg-brown-80%-main text-white'
                    : 'bg-white text-textColorText2 hover:bg-brandPrimary-10%'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>

          {isLoading ? (
            <div className="py-80px text-center text-style-body2 text-textColorText2">
              로딩 중...
            </div>
          ) : (
            <div className="max-w-[900px] flex flex-col gap-24px">
              <div className="bg-white rounded-16px shadow-sm p-24px flex gap-40px">
                <div>
                  <div className="text-style-captionL text-textColorText2">
                    전체 사용자
                  </div>
                  <div className="font-pretendard font-bold text-24px text-textColorText2">
                    {total.toLocaleString()}
                  </div>
                </div>
                <div>
                  <div className="text-style-captionL text-textColorText2">
                    버전이 확인된 사용자
                  </div>
                  <div className="font-pretendard font-bold text-24px text-textColorText2">
                    {knownTotal.toLocaleString()}
                  </div>
                </div>
                <div>
                  <div className="text-style-captionL text-textColorText2">
                    모름
                  </div>
                  <div className="font-pretendard font-bold text-24px text-textColorText2">
                    {unknown.toLocaleString()}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-16px shadow-sm overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-brandPrimary-10%">
                      {['플랫폼', '버전', '빌드', '사용자', '비율', '마지막 접속'].map(
                        (h) => (
                          <th
                            key={h}
                            className="px-16px py-12px text-left text-style-captionL text-textColorText2"
                          >
                            {h}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 && (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-16px py-40px text-center text-style-body2 text-textColorText2"
                        >
                          아직 버전을 보낸 앱이 없습니다
                        </td>
                      </tr>
                    )}
                    {rows.map((r, i) => (
                      <tr
                        key={`${r.platform}-${r.version}-${r.build}-${i}`}
                        className="border-b border-brandPrimary-10% last:border-b-0"
                      >
                        <td className="px-16px py-12px text-style-body2 text-textColorText2">
                          {r.platform ? PLATFORM_LABEL[r.platform] ?? r.platform : '—'}
                        </td>
                        <td className="px-16px py-12px text-style-body2 text-textColorText2">
                          {r.version ?? '모름'}
                        </td>
                        <td className="px-16px py-12px text-style-body2 text-textColorText2">
                          {r.build ?? '-'}
                        </td>
                        <td className="px-16px py-12px text-style-body2 text-textColorText2">
                          {r.users.toLocaleString()}
                        </td>
                        <td className="px-16px py-12px text-style-body2 text-textColorText2">
                          {total > 0
                            ? `${((r.users / total) * 100).toFixed(1)}%`
                            : '-'}
                        </td>
                        <td className="px-16px py-12px text-style-body2 text-textColorText2">
                          {formatDate(r.lastSeen)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  )
}

export default AdminAppVersions
